import { Link } from "react-router-dom";
import { sourceShortLabel } from "../features/leads/leadFilterLabels";
import { Card } from "../components/ui/Card";
import { StatusPill } from "../components/ui/StatusPill";
import {
  useOverviewQuery,
  useFollowUpsDueQuery,
} from "../features/analytics/api";
import { LatestLeadsCard } from "../features/analytics/LatestLeadsCard";
import { useMyMemberDetail } from "../features/members/api";
import { useWorkspace } from "../features/workspace/WorkspaceContext";
import { useAuth } from "../features/auth/AuthProvider";
import { useMembersIndex } from "../features/team/useMembersIndex";
import {
  formatRelative,
  formatFollowUpDate,
  isFollowUpOverdue,
} from "../lib/format";

const STATUS_ORDER = ["new", "interested", "follow_up", "not_interested", "converted", "lost"];
const STATUS_COLORS: Record<string, string> = {
  new: "#3b82f6",
  interested: "#8b5cf6",
  follow_up: "#f59e0b",
  not_interested: "#94a3b8",
  converted: "#16a34a",
  lost: "#dc2626",
};
const SOURCE_COLORS = ["#3b82f6", "#16a34a", "#8b5cf6", "#f59e0b", "#dc2626", "#0ea5e9", "#ec4899", "#64748b"];

export default function DashboardPage() {
  const { user } = useAuth();
  // Sales reps see a personal dashboard (their own stats, leads, mix).
  // Admins/managers/superadmins see the workspace-wide rollup.
  const isSalesView = !!user && !user.isSuperadmin && user.role === "sales";
  return isSalesView ? <SalesDashboard /> : <TeamDashboard />;
}

// ────────────────────────────────────────────────────────────
// Team / manager view (workspace-wide)
// ────────────────────────────────────────────────────────────

function TeamDashboard() {
  const { current } = useWorkspace();
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useOverviewQuery(current?.slug ?? null, 30);
  // Top performers card is manager+ only — mirrors the gating on the
  // dedicated /members page (sales doesn't see other teammates' metrics).
  const canSeeMembers =
    !!user && (user.isSuperadmin || user.role === "manager" || user.role === "admin");

  if (isLoading) {
    return (
      <div className="page">
        <header className="page-header">
          <h1 className="page-title">Dashboard</h1>
        </header>
        <div className="skeleton-rows">Loading…</div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="page">
        <div className="form-error">{(error as Error).message}</div>
      </div>
    );
  }
  if (!data) return null;

  const funnel = STATUS_ORDER.map((s) => ({
    status: s,
    label: s.replace("_", " "),
    count: data.funnel.find((f) => f.status === s)?.count ?? 0,
  }));
  const funnelMax = Math.max(1, ...funnel.map((f) => f.count));
  const funnelTotal = funnel.reduce((sum, f) => sum + f.count, 0);

  const sourceData = data.sources
    .slice(0, 8)
    .map((s) => ({
      name: s.campaign ?? `${sourceShortLabel(s.source)} (uncategorised)`,
      value: s.count,
    }))
    .filter((s) => s.value > 0);
  const sourceTotal = sourceData.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {current?.name ?? "—"} · last {data.days} days
          </p>
        </div>
      </header>

      <div className="stat-grid">
        <StatTile label="Total leads" value={data.stats.total.toLocaleString()} />
        <StatTile label="Converted" value={data.stats.converted.toLocaleString()} accent="ok" />
        <StatTile
          label="Conversion rate"
          value={`${(data.stats.convertedRate * 100).toFixed(1)}%`}
        />
        <StatTile
          label="Unattended >24h"
          value={data.stats.unattended24h.toLocaleString()}
          accent={data.stats.unattended24h > 0 ? "warn" : undefined}
        />
        <StatTile label="New today" value={data.stats.newToday.toLocaleString()} />
      </div>

      <FollowUpsDueCard showAssignee />

      <div className="dashboard-grid">
        <Card title="Funnel" subtitle={`${funnelTotal.toLocaleString()} leads across statuses`}>
          {funnelTotal === 0 ? (
            <p className="muted">No leads in this range.</p>
          ) : (
            <ul className="funnel-list">
              {funnel.map((row) => {
                const pctOfMax = (row.count / funnelMax) * 100;
                const pctOfTotal = funnelTotal > 0 ? (row.count / funnelTotal) * 100 : 0;
                return (
                  <li key={row.status} className="funnel-row">
                    <div className="funnel-row-head">
                      <span className="funnel-label">{row.label}</span>
                      <span className="funnel-count">
                        {row.count.toLocaleString()}
                        <span className="muted"> · {pctOfTotal.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className="funnel-bar-track">
                      <div
                        className="funnel-bar-fill"
                        style={{
                          width: `${pctOfMax}%`,
                          background: STATUS_COLORS[row.status] ?? "#3b82f6",
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title="Top sources" subtitle={`${sourceTotal.toLocaleString()} leads · top ${sourceData.length}`}>
          {sourceData.length === 0 ? (
            <p className="muted">No leads in this range.</p>
          ) : (
            <ul className="source-list">
              {sourceData.map((s, i) => {
                const pct = sourceTotal > 0 ? (s.value / sourceTotal) * 100 : 0;
                const color = SOURCE_COLORS[i % SOURCE_COLORS.length];
                return (
                  <li key={s.name} className="source-row">
                    <div className="source-row-head">
                      <span className="source-name">
                        <span className="source-swatch" style={{ background: color }} />
                        {s.name}
                      </span>
                      <span className="source-count">
                        {s.value.toLocaleString()}
                        <span className="muted"> · {pct.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className="funnel-bar-track">
                      <div
                        className="funnel-bar-fill"
                        style={{ width: `${pct}%`, background: color }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {canSeeMembers ? (
        <Card
          title="Top performers"
          subtitle="By leads assigned in the last 30 days"
          right={
            <Link to="/members" className="btn btn-secondary btn-sm">
              View all →
            </Link>
          }
        >
          {data.leaderboard.length === 0 ? (
            <p className="muted">No team activity in this range.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{ textAlign: "right" }}>Leads</th>
                    <th style={{ textAlign: "right" }}>Activities</th>
                    <th style={{ textAlign: "right" }}>Converted</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leaderboard.slice(0, 5).map((row) => (
                    <tr key={row.userId}>
                      <td>
                        <Link to={`/members/${row.userId}`} className="row-link">
                          {row.name}
                        </Link>
                        <div className="row-sub">{row.email}</div>
                      </td>
                      <td style={{ textAlign: "right" }}>{row.leadCount}</td>
                      <td style={{ textAlign: "right" }}>{row.activityCount}</td>
                      <td style={{ textAlign: "right" }}>{row.convertedCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}

      <LatestLeadsCard
        direct={data.latestDirect ?? []}
        meta={data.latestMeta ?? []}
        attention={data.hot ?? []}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Sales personal view (just-me)
// Layout: stats card → assigned leads table → analytics card.
// ────────────────────────────────────────────────────────────

function SalesDashboard() {
  const { current } = useWorkspace();
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useMyMemberDetail(
    current?.slug ?? null,
    30
  );

  if (isLoading) {
    return (
      <div className="page">
        <header className="page-header">
          <h1 className="page-title">My dashboard</h1>
        </header>
        <div className="skeleton-rows">Loading…</div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="page">
        <div className="form-error">{(error as Error).message}</div>
      </div>
    );
  }
  if (!data) return null;

  const { metrics, assignedLeads, funnel, sources } = data;

  const funnelRows = STATUS_ORDER.map((s) => ({
    status: s,
    label: s.replace("_", " "),
    count: funnel.find((f) => f.status === s)?.count ?? 0,
  }));
  const funnelMax = Math.max(1, ...funnelRows.map((r) => r.count));
  const funnelTotal = funnelRows.reduce((sum, r) => sum + r.count, 0);

  const sourceRows = sources
    .map((s) => ({
      name:
        s.campaign ?? `${sourceShortLabel(s.source)} (uncategorised)`,
      value: s.count,
    }))
    .filter((s) => s.value > 0);
  const sourceTotal = sourceRows.reduce((sum, r) => sum + r.value, 0);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">My dashboard</h1>
          <p className="page-subtitle">
            {user?.name ?? "Me"} · {current?.name ?? "—"} · last {data.days} days
          </p>
        </div>
      </header>

      <div className="stat-grid">
        <StatTile
          label="Active leads"
          value={metrics.activeLeads.toLocaleString()}
        />
        <StatTile
          label="New (30d)"
          value={metrics.newLeads.toLocaleString()}
        />
        <StatTile
          label="Activities"
          value={metrics.activitiesLogged.toLocaleString()}
        />
        <StatTile
          label="Converted"
          value={metrics.converted.toLocaleString()}
          accent="ok"
        />
        <StatTile
          label="Stale >24h"
          value={metrics.unattended24h.toLocaleString()}
          accent={metrics.unattended24h > 0 ? "warn" : undefined}
        />
      </div>

      <FollowUpsDueCard />

      <Card
        title="My assigned leads"
        subtitle={`${assignedLeads.length} open lead${assignedLeads.length === 1 ? "" : "s"} (excluding converted/lost)`}
        right={
          <Link to="/leads" className="btn btn-secondary btn-sm">
            All leads →
          </Link>
        }
      >
        {assignedLeads.length === 0 ? (
          <p className="muted">No open leads assigned to you right now.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Last activity</th>
                  <th>Received</th>
                </tr>
              </thead>
              <tbody>
                {assignedLeads.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/leads/${row.id}`} className="row-link">
                        {row.name ?? "(no name)"}
                      </Link>
                      {row.company ? (
                        <div className="row-sub">{row.company}</div>
                      ) : null}
                    </td>
                    <td>
                      <StatusPill status={row.status} />
                    </td>
                    <td>
                      <span className="badge">
                        {sourceShortLabel(row.source)}
                      </span>
                    </td>
                    <td title={row.lastActivityAt ?? ""}>
                      {row.lastActivityAt
                        ? formatRelative(row.lastActivityAt)
                        : "—"}
                    </td>
                    <td title={row.createdAt}>
                      {formatRelative(row.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="dashboard-grid">
        <Card
          title="My funnel"
          subtitle={`${funnelTotal.toLocaleString()} leads received in range`}
        >
          {funnelTotal === 0 ? (
            <p className="muted">No leads received in this range.</p>
          ) : (
            <ul className="funnel-list">
              {funnelRows.map((row) => {
                const pctOfMax = (row.count / funnelMax) * 100;
                const pctOfTotal =
                  funnelTotal > 0 ? (row.count / funnelTotal) * 100 : 0;
                return (
                  <li key={row.status} className="funnel-row">
                    <div className="funnel-row-head">
                      <span className="funnel-label">{row.label}</span>
                      <span className="funnel-count">
                        {row.count.toLocaleString()}
                        <span className="muted">
                          {" "}
                          · {pctOfTotal.toFixed(0)}%
                        </span>
                      </span>
                    </div>
                    <div className="funnel-bar-track">
                      <div
                        className="funnel-bar-fill"
                        style={{
                          width: `${pctOfMax}%`,
                          background: STATUS_COLORS[row.status] ?? "#3b82f6",
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card
          title="My top sources"
          subtitle={`${sourceTotal.toLocaleString()} leads · top ${sourceRows.length}`}
        >
          {sourceRows.length === 0 ? (
            <p className="muted">No sources to break down yet.</p>
          ) : (
            <ul className="source-list">
              {sourceRows.map((s, i) => {
                const pct = sourceTotal > 0 ? (s.value / sourceTotal) * 100 : 0;
                const color = SOURCE_COLORS[i % SOURCE_COLORS.length];
                return (
                  <li key={`${s.name}-${i}`} className="source-row">
                    <div className="source-row-head">
                      <span className="source-name">
                        <span
                          className="source-swatch"
                          style={{ background: color }}
                        />
                        {s.name}
                      </span>
                      <span className="source-count">
                        {s.value.toLocaleString()}
                        <span className="muted"> · {pct.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className="funnel-bar-track">
                      <div
                        className="funnel-bar-fill"
                        style={{ width: `${pct}%`, background: color }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

// Leads with a follow-up due today or earlier. Server scopes by role (own for
// sales, workspace-wide for managers/admins). `showAssignee` adds the assignee
// column on the workspace-wide view.
function FollowUpsDueCard({ showAssignee }: { showAssignee?: boolean }) {
  const { current } = useWorkspace();
  const { resolveName } = useMembersIndex();
  const { data, isLoading } = useFollowUpsDueQuery(current?.slug ?? null);
  const leads = data?.leads ?? [];

  return (
    <Card
      title="Follow-ups due"
      subtitle={
        isLoading
          ? "Loading…"
          : `${leads.length} lead${leads.length === 1 ? "" : "s"} due today or earlier`
      }
    >
      {leads.length === 0 ? (
        <p className="muted">Nothing due. You're all caught up.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Status</th>
                {showAssignee ? <th>Assigned to</th> : null}
                <th>Follow-up</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Link to={`/leads/${row.id}`} className="row-link">
                      {row.name ?? "(no name)"}
                    </Link>
                    {row.phoneE164 ? (
                      <div className="row-sub">{row.phoneE164}</div>
                    ) : null}
                  </td>
                  <td>
                    <StatusPill status={row.status} />
                  </td>
                  {showAssignee ? (
                    <td>
                      {row.assignedTo ? (
                        resolveName(row.assignedTo)
                      ) : (
                        <span className="muted">Unassigned</span>
                      )}
                    </td>
                  ) : null}
                  <td title={row.nextFollowUpAt}>
                    <span
                      className={
                        isFollowUpOverdue(row.nextFollowUpAt)
                          ? "followup-due followup-overdue"
                          : "followup-due"
                      }
                    >
                      {formatFollowUpDate(row.nextFollowUpAt)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "ok" | "warn" | "err";
}) {
  return (
    <div className={`stat-tile ${accent ? `accent-${accent}` : ""}`.trim()}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}
